// src/utils/validation.ts
//
// Lightweight, dependency-free client-side validators. These mirror the
// backend Zod rules (src/utils/validators.ts) so users get instant feedback
// and the server stays the source of truth. Keep the two in sync.

export type FieldResult = { valid: boolean; message?: string };

/** RFC-ish email check (matches backend zEmail intent). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEmail(value: string): FieldResult {
  const v = (value || '').trim();
  if (!v) return { valid: false, message: 'Email is required' };
  if (v.length > 254) return { valid: false, message: 'Email is too long' };
  if (!EMAIL_RE.test(v)) return { valid: false, message: 'Enter a valid email address' };
  return { valid: true };
}

/** Presence + length only (strength is scored separately by the strength meter). */
export function validatePassword(value: string): FieldResult {
  if (!value) return { valid: false, message: 'Password is required' };
  if (value.length < 8) return { valid: false, message: 'Password must be at least 8 characters' };
  if (value.length > 128) return { valid: false, message: 'Password is too long' };
  return { valid: true };
}

export function validateRequired(value: string | undefined | null, label = 'This field'): FieldResult {
  if (!value || !String(value).trim()) return { valid: false, message: `${label} is required` };
  return { valid: true };
}

export function validateMatch(a: string, b: string, label = 'Passwords'): FieldResult {
  if (a !== b) return { valid: false, message: `${label} do not match` };
  return { valid: true };
}

/** Country *code*: exactly two ASCII letters (e.g. "ca"). */
export function validateCountryCode(value: string): FieldResult {
  const v = (value || '').trim().toLowerCase();
  if (!v) return { valid: false, message: 'Country is required' };
  if (!/^[a-z]{2}$/.test(v)) return { valid: false, message: 'Country code must be a 2-letter code (e.g. "ca")' };
  return { valid: true };
}

export function validatePhone(value: string): FieldResult {
  const v = (value || '').trim();
  if (!v) return { valid: true }; // optional
  if (!/^[+]?[\d\s().-]{7,20}$/.test(v)) return { valid: false, message: 'Enter a valid phone number' };
  return { valid: true };
}

export function validateUrl(value: string): FieldResult {
  const v = (value || '').trim();
  if (!v) return { valid: true }; // optional
  try {
    const u = new URL(v);
    if (!/^https?:$/.test(u.protocol)) return { valid: false, message: 'URL must start with http:// or https://' };
    return { valid: true };
  } catch {
    return { valid: false, message: 'Enter a valid URL' };
  }
}

/**
 * Run a map of { field: FieldResult } and return the first error message plus a
 * per-field error object suitable for rendering inline.
 */
export function collectErrors(results: Record<string, FieldResult>): {
  ok: boolean;
  errors: Record<string, string>;
  firstMessage?: string;
} {
  const errors: Record<string, string> = {};
  let firstMessage: string | undefined;
  for (const [field, r] of Object.entries(results)) {
    if (!r.valid && r.message) {
      errors[field] = r.message;
      if (!firstMessage) firstMessage = r.message;
    }
  }
  return { ok: Object.keys(errors).length === 0, errors, firstMessage };
}
